









































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from "@vue/composition-api";
import RightDrawerHeader from "@/components/Pages/Scout/elements/RightDrawerHeader/index.vue";
import SingleSelectSearchTextField from "@/components/Pages/Scout/elements/SingleSelectSearchTextField/index.vue";
import ExternalLinkButton from "@/components/Pages/Scout/elements/ExternalLinkButton/index.vue";
import TextArea from "@/components/Atoms/Forms/TextArea/index.vue";
import OccTooltipButton from "@/components/Atoms/OccTooltipButton/index.vue";
import { occPrimary, occWhite } from "@/assets/styles/vars-for-script.scss";
import { SelectItem } from "@/components/Pages/Scout/elements/SingleSelectSearchTextField/types";
import { getAllMessageTemplates } from "@/grpc-web/scout/message_template";
import { MessageTemplate } from "@/pb/scout/message_template/v1/message_template_pb";
import { getCharacterCount } from "@/helpers/string-helper";
import { sendThreadComment } from "@/grpc-web/scout/message";
import { ThreadMessage } from "@/components/Pages/Scout/Messages/ScoutMessageLargePanel/types";
import {
  setItemToLocalStorage,
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  ScoutItem
} from "@/components/Pages/Scout/helpers/localstorage-helper";
import { getScoutCommentStorageKey } from "@/components/Pages/Scout/Messages/ScoutMessageLargePanel/methods";

export default defineComponent({
  name: "ScoutMessageLargePanel",
  components: {
    RightDrawerHeader,
    TextArea,
    OccTooltipButton,
    SingleSelectSearchTextField,
    ExternalLinkButton
  },
  emits: ["send-reservation", "send"],
  props: {
    width: {
      type: String,
      default: "912px"
    },
    messageThreadId: {
      type: Number,
      required: true
    },
    reset: {
      type: Boolean,
      default: false
    },
    lastMessageAlreadySent: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const currentThreadId = ref<number>(props.messageThreadId);
    const messageText = ref<string>("");
    const selectedMessageTemplateId = ref<string | undefined>(undefined);
    const messageTemplates = ref<MessageTemplate[]>([]);
    const textareaComponentRef = ref<HTMLTextAreaElement | null>(null);
    const messageTemplateSelectList = computed<SelectItem[]>(() => {
      return messageTemplates.value.map((opt: MessageTemplate) => {
        return { value: String(opt.getId()), label: opt.getName() };
      });
    });
    const widthStyle = computed(() => {
      return { width: props.width };
    });

    const currentInputtedMessageCharactersCount = computed<number>(() => {
      return getCharacterCount(messageText.value);
    });

    const restoreCommentFromLocalStorage = () => {
      // テンプレートを利用している場合、復元時にロードが終わってない可能性があるためここでロードする
      if (messageTemplates.value.length == 0) {
        loadMessageTemplates();
      }
      const storedComment = getItemFromLocalStorage(
        getScoutCommentStorageKey(),
        props.messageThreadId
      );
      selectedMessageTemplateId.value = storedComment?.templateId || undefined;
      messageText.value = storedComment?.text || "";
    };

    watch(
      () => props.messageThreadId,
      () => {
        if (props.messageThreadId !== currentThreadId.value) {
          selectedMessageTemplateId.value = "";
          messageText.value = "";
        }
        restoreCommentFromLocalStorage();
      }
    );

    onMounted(() => {
      restoreCommentFromLocalStorage();
    });

    const maxMessageTextCount = 5000;
    const messageCharCounter = computed<string>(() => {
      return `${currentInputtedMessageCharactersCount.value} / ${maxMessageTextCount}文字`;
    });

    const isInvalidMessageText = computed<boolean>(() => {
      return currentInputtedMessageCharactersCount.value > maxMessageTextCount;
    });

    const charCounterClasses = computed<string[]>(() => {
      if (isInvalidMessageText.value) {
        return ["scout-message-panel__char-counter__alert"];
      }
      return [];
    });

    const sendButtonDisabled = computed<boolean>(() => {
      return (
        messageText.value.length === 0 ||
        isInvalidMessageText.value ||
        !props.lastMessageAlreadySent
      );
    });

    const loadMessageTemplates = async () => {
      messageTemplates.value = await getAllMessageTemplates();
    };

    const handleOpenedPullDown = () => {
      loadMessageTemplates();
    };

    const handleInputtedMessageText = (value: string) => {
      messageText.value = value;
    };

    const handleClickedSendReservation = () => {
      const messageData: ThreadMessage = {
        threadId: props.messageThreadId,
        text: messageText.value,
        selectedMessageTemplateId:
          typeof selectedMessageTemplateId.value === "string" &&
          Number(selectedMessageTemplateId.value) > 0
            ? Number(selectedMessageTemplateId.value)
            : undefined
      };
      emit("send-reservation", messageData);
    };

    watch(
      () => props.reset,
      newValue => {
        if (newValue !== undefined) {
          clearInput();
        }
      }
    );

    const handleClickedSend = async () => {
      await sendThreadComment(props.messageThreadId, messageText.value);
      emit("send");
      clearInput();
    };

    const clearInput = () => {
      messageText.value = "";
      selectedMessageTemplateId.value = "";
      removeItemFromLocalStorage(
        getScoutCommentStorageKey(),
        props.messageThreadId
      );
    };

    const handleChangedSelectedTemplate = (value: string) => {
      if (
        messageText.value.length > 0 &&
        !confirm("入力中の内容は上書きされます。よろしいですか？")
      ) {
        return;
      }

      selectedMessageTemplateId.value = value;
      const selectedTemplate = messageTemplates.value.find(
        template => String(template.getId()) === value
      );
      if (!selectedTemplate) {
        return;
      }

      messageText.value = selectedTemplate.getBody();

      // Textareaにカーソル当てる
      if (textareaComponentRef.value) {
        const textareaElement = textareaComponentRef.value as HTMLTextAreaElement;
        textareaElement.focus();
      }
    };

    const isInputMode = ref<boolean>(false);
    const textareaClasses = computed(() => {
      const classes = [];
      if (isInputMode.value) {
        classes.push("input");
      }
      return classes;
    });
    const handleFocusedTextarea = () => {
      isInputMode.value = true;
    };
    const handleBlurredTextarea = () => {
      saveInputtedMessage(props.messageThreadId);
      isInputMode.value = false;
    };

    const handleMouseOverMessageLargePanel = () => {
      emit("mouseover");
    };

    const handleMouseLeaveMessageLargePanel = () => {
      emit("mouseleave");
    };

    // メッセージ内容があったら保存する
    const saveInputtedMessage = (messageThreadId: number) => {
      if (messageText.value && messageThreadId) {
        const item: ScoutItem = {
          id: messageThreadId,
          templateId: selectedMessageTemplateId.value || undefined,
          text: messageText.value
        };
        setItemToLocalStorage(getScoutCommentStorageKey(), item);
      }
    };

    const sendButtonTooltipMessage = computed<string>(() => {
      if (!props.lastMessageAlreadySent) {
        return "最新のメッセージが送信前のため、新規メッセージを送信できません。";
      }

      return "";
    });

    return {
      occPrimary,
      occWhite,
      widthStyle,
      messageText,
      messageTemplateSelectList,
      selectedMessageTemplateId,
      messageCharCounter,
      charCounterClasses,
      sendButtonDisabled,
      textareaClasses,
      textareaComponentRef,
      handleOpenedPullDown,
      handleChangedSelectedTemplate,
      handleInputtedMessageText,
      handleFocusedTextarea,
      handleBlurredTextarea,
      handleClickedSendReservation,
      handleClickedSend,
      handleMouseOverMessageLargePanel,
      handleMouseLeaveMessageLargePanel,
      sendButtonTooltipMessage
    };
  }
});
